import { useState } from 'react'
import { X, Star, Loader2 } from 'lucide-react'
import supportService from '@/features/support/services/supportService'

interface FreeTestFeedbackModalProps {
  testCode: string
  isOpen: boolean
  onClose: () => void
  onSubmitSuccess?: () => void
}

const PRICE_OPTIONS = ['₹499', '₹999', '₹1499', 'Would not pay'] as const
const TEST_OPTIONS = ['Quick (45 min)', 'Full Length (180 min)'] as const

export default function FreeTestFeedbackModal({
  testCode,
  isOpen,
  onClose,
  onSubmitSuccess,
}: FreeTestFeedbackModalProps) {
  const [difficultyRating, setDifficultyRating] = useState<number | null>(null)
  const [testPreference, setTestPreference] = useState<string | null>(null)
  const [missingFeedback, setMissingFeedback] = useState('')
  const [pricePreference, setPricePreference] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) return null

  const handleSubmit = async () => {
    if (!difficultyRating) {
      setError('Please rate the difficulty')
      return
    }

    setIsSubmitting(true)
    setError(null)

    const message = `Test Feedback - ${testCode}

Q1: Was the difficulty NEET-relevant?
A1: ${difficultyRating}/5

Q2: Which test helped more?
A2: ${testPreference || 'Not answered'}

Q3: What felt missing?
A3: ${missingFeedback || 'Not answered'}

Q4: Would you pay for more such tests?
A4: ${pricePreference || 'Not answered'}`

    try {
      await supportService.submitFeedback({
        type: 'FEEDBACK',
        category: 'OTHER',
        priority: 'MEDIUM',
        subject: `Free Test Feedback - ${testCode}`,
        message,
      })

      // Mark as submitted in localStorage
      localStorage.setItem(`free_test_feedback_${testCode}`, 'true')

      onSubmitSuccess?.()
      onClose()
    } catch (err) {
      setError('Failed to submit feedback. Please try again.')
      console.error('Failed to submit feedback:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSkip = () => {
    // Mark as skipped in localStorage so we don't show again
    localStorage.setItem(`free_test_feedback_${testCode}`, 'skipped')
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-50">
      {/* Header with close button */}
      <header className="bg-white border-b border-gray-200 px-4 py-4 flex-shrink-0">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900">Quick Feedback</h1>
          <button
            onClick={handleSkip}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close and view results"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          <p className="text-gray-600 text-center">
            Help us improve your test experience by sharing your feedback.
          </p>

          {/* Q1: Difficulty Rating */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              Was the difficulty NEET-relevant?
            </label>
            <div className="flex gap-3 justify-center">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  onClick={() => setDifficultyRating(rating)}
                  className={`p-3 rounded-xl transition-colors ${
                    difficultyRating && rating <= difficultyRating
                      ? 'bg-yellow-100 text-yellow-600'
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  <Star
                    className={`w-8 h-8 ${difficultyRating && rating <= difficultyRating ? 'fill-current' : ''}`}
                  />
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-3 text-center">1 = Not at all, 5 = Very relevant</p>
          </div>

          {/* Q2: Test Preference */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              Which test format helped more?
            </label>
            <div className="flex gap-3">
              {TEST_OPTIONS.map((option) => (
                <button
                  key={option}
                  onClick={() => setTestPreference(option)}
                  className={`flex-1 px-4 py-3 text-sm rounded-xl border-2 transition-colors ${
                    testPreference === option
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/* Q3: Missing Feedback */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              What felt missing? (Optional)
            </label>
            <textarea
              value={missingFeedback}
              onChange={(e) => setMissingFeedback(e.target.value)}
              placeholder="Share your thoughts..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={4}
            />
          </div>

          {/* Q4: Price Preference */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              Would you pay for more such tests?
            </label>
            <div className="grid grid-cols-2 gap-3">
              {PRICE_OPTIONS.map((option) => (
                <button
                  key={option}
                  onClick={() => setPricePreference(option)}
                  className={`px-4 py-3 text-sm rounded-xl border-2 transition-colors ${
                    pricePreference === option
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-xl">{error}</p>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 px-4 py-4 flex-shrink-0">
        <div className="max-w-2xl mx-auto flex gap-3">
          <button
            onClick={handleSkip}
            className="flex-1 px-4 py-3 text-base text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
          >
            Skip & View Results
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-4 py-3 text-base text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Feedback'
            )}
          </button>
        </div>
      </footer>
    </div>
  )
}
